"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createClient = createClient;
exports.createMemoryAdapter = createMemoryAdapter;
exports.createWebRTCAdapter = createWebRTCAdapter;
exports.createJsonSerdes = createJsonSerdes;
const util_typescript_1 = require("@varos/util-typescript");
const message_1 = require("./message");
const clientSchema_1 = require("./clientSchema");
// Utility: Generate a temporary ID for pending messages.
function generateTempId() {
    return 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
}
function createClient(init, enhancer) {
    return _create(init, enhancer);
}
function _create(config, enhancer) {
    if (enhancer) {
        return enhancer(_create)(config);
    }
    const { adapter: { serdes, connection, message: messageStore }, } = config;
    function debug(...args) {
        const final = [`[Client]`, ...args];
        return console.debug(final);
    }
    const toolsByName = config.tools.reduce((acc, tool) => ({ ...acc, [tool.definition.name]: tool }), {});
    // Singleton variable to hold the speech duration
    let speechDuration = null;
    let audio_start_ms = null;
    // FIFO buffer for pending requests (user messages not yet confirmed)
    const pendingRequests = [];
    // [ADD] Buffer for pending voice messages awaiting transcription
    const pendingVoiceMessages = [];
    // Separate pending requests for directives to avoid conflicts
    const pendingDirectives = [];
    let isConnected = false; // Track connection status
    let bufferedMessages = []; // Buffer for messages sent before connection
    // FIFO buffer for pending function calls
    const pendingFunctionCalls = {};
    const handleRawMessage = async (raw) => {
        const msg = serdes.deserialize(raw);
        // console.log('msg', msg);
        const parsed = clientSchema_1.EventSchema.safeParse(msg);
        if (!parsed.success) {
            // console.warn('failed to parse message', raw);
            return;
        }
        const { data } = parsed;
        // console.log('received message', parsed);
        if (data.type === 'response.output_item.done' &&
            data.item.type === 'function_call') {
            console.log('function call', data);
            const tool = toolsByName[data.item.name];
            if (!tool) {
                console.error(`tool ${data.item.name} not found`, toolsByName);
                return;
            }
            const json = JSON.parse(data.item.arguments);
            let input = null;
            if (tool.schema) {
                const parsed = tool.schema.safeParse(json);
                if (!parsed.success) {
                    console.error(`failed to parse function call arguments`, data.item.arguments, parsed.error);
                    return;
                }
                input = parsed.data;
            }
            else {
                input = json;
            }
            const response = await tool.apply(input);
            console.log('tool response', response);
            sendRaw({
                type: 'conversation.item.create',
                item: {
                    type: 'function_call_output',
                    call_id: data.item.call_id,
                    output: JSON.stringify(response ?? null),
                },
            });
            sendRaw({
                type: 'response.create',
            });
        }
        if (data.type === 'session.updated') {
            console.info('Session updated', data.session);
            return;
        }
        if (data.type === 'input_audio_buffer.speech_started') {
            console.log('speech started', data);
            // Reset speech duration when speech starts
            speechDuration = null;
            audio_start_ms = data.audio_start_ms;
        }
        if (data.type === 'input_audio_buffer.speech_stopped') {
            console.log('speech stopped', data);
            // Calculate the speech duration when speech stops
            // assert(audio_start_ms, 'audio_start_ms not found');
            const startTime = audio_start_ms;
            const durationMs = startTime ? data.audio_end_ms - startTime : 0;
            speechDuration = durationMs;
        }
        // Handle system responses containing assistant replies.
        if (data.type === 'response.done') {
            console.log('response.done received', data);
            console.log('pendingRequests length at response.done:', pendingRequests.length);
            // Build assistant reply messages from the response output.
            const assistantMessages = data.response.output.flatMap((item, index) => {
                if (item.type === 'message') {
                    return [
                        {
                            id: [data.response.id, index.toString()].join('-'),
                            role: 'system',
                            kind: 'text',
                            content: item.content
                                .flatMap((fragment) => {
                                if (fragment.type === 'text') {
                                    return [fragment.text];
                                }
                                if (fragment.type === 'audio') {
                                    return [fragment.transcript];
                                }
                                return [];
                            })
                                .join(' '),
                            committed: true,
                        },
                    ];
                }
                return [];
            });
            // If there is a pending request waiting for a reply, assume this response corresponds
            // to the oldest pending request.
            if (pendingRequests.length > 0) {
                console.log('Resolving pending request', pendingRequests.length, 'remaining');
                console.log('Pending request details:', pendingRequests[0]);
                const pending = pendingRequests.shift();
                pending.assistantReplies.push(...assistantMessages);
                for (const m of assistantMessages) {
                    messageStore.put(m);
                    emit('message', m);
                }
                const result = {
                    userMessage: pending.userMessage,
                    assistantReplies: pending.assistantReplies,
                };
                console.log('Resolving with result:', result);
                pending.resolve(result);
                console.log('Pending request resolved, messages:', assistantMessages.length);
            }
            else {
                console.log('No pending request to resolve, pendingRequests is empty');
                for (const m of assistantMessages) {
                    messageStore.put(m);
                    emit('message', m);
                }
            }
            // Also check if we have any pending directives to resolve
            if (pendingDirectives.length > 0) {
                console.log(`Resolving ${pendingDirectives.length} pending directives on response.done`);
                // Resolve all pending directives
                pendingDirectives.forEach((directive) => {
                    directive.resolve();
                });
                // Clear the array
                pendingDirectives.length = 0;
            }
            return;
        }
        // Handle backend confirmation for a user message.
        if (data.type === 'conversation.item.created') {
            console.log('data', data);
            const [content] = data.item.content;
            if (content?.type === 'input_audio') {
                // assert(speechDuration, 'speech duration not found');
                console.log('voice message received', data);
                // Instead of emitting immediately, store in pending buffer
                const audioMessage = (0, message_1.buildMessage)({
                    id: data.item.id,
                    role: 'user',
                    kind: 'audio',
                    transcript: '', // Start with empty transcript
                    durationMs: speechDuration,
                    committed: true,
                });
                // Store in pending voice messages
                pendingVoiceMessages.push({
                    id: data.item.id,
                    message: audioMessage,
                });
                console.log('Added voice message to pending buffer, waiting for transcription', data.item.id);
                // Don't emit or add to message store yet
            }
            else if (pendingRequests.length > 0) {
                const pending = pendingRequests[0]; // do not shift yet; wait for assistant reply
                (0, util_typescript_1.assert)(pending, 'pending request not found');
                // assert(pending.userMessage, 'pending user message not found');
                if (!pending.userMessage) {
                    console.warn('No pending user message to confirm for event:', data);
                    return;
                }
                const oldTempId = pending.userMessage.id;
                // Update the placeholder with the final item id.
                const updatedUserMessage = {
                    ...pending.userMessage,
                    // id: data.item.id,
                };
                pending.userMessage = updatedUserMessage;
                // Remove the old placeholder entry.
                messageStore.delete(oldTempId);
                // Add the updated message.
                messageStore.put(updatedUserMessage);
                // Emit an "update" event.
                emit('update', updatedUserMessage);
            }
            else {
                console.warn('No pending user message to confirm for event:', data);
            }
            return;
        }
        // Handle transcription events.
        if (data.type === 'conversation.item.input_audio_transcription.completed') {
            console.log('Transcription completed for item_id:', data.item_id, 'with transcript:', data.transcript);
            // Find message in pending voice messages buffer
            const pendingIndex = pendingVoiceMessages.findIndex((item) => item.id === data.item_id);
            if (pendingIndex !== -1) {
                // Found in pending buffer, update transcript and emit
                const pendingItem = pendingVoiceMessages[pendingIndex];
                // Remove from pending buffer
                pendingVoiceMessages.splice(pendingIndex, 1);
                // Make sure pendingItem exists and is the right type
                if (pendingItem && pendingItem.message.kind === 'audio') {
                    // Create a complete message with the transcript
                    const completeMessage = {
                        ...pendingItem.message,
                        transcript: data.transcript,
                    };
                    console.log('Adding voice message with transcript to message store', completeMessage);
                    // Now add to message store and emit
                    messageStore.put(completeMessage);
                    emit('message', completeMessage);
                }
                else if (pendingItem) {
                    console.warn('Invalid voice message type in pending buffer', pendingItem);
                }
                else {
                    console.warn('Pending item is undefined, which should not happen');
                }
            }
            else {
                // Fallback to original behavior for backward compatibility
                let target = messageStore.scan().find((m) => m.id === data.item_id);
                if (!target && pendingRequests.length > 0) {
                    const userMessage = pendingRequests[0]?.userMessage;
                    if (userMessage) {
                        target = userMessage;
                    }
                }
                if (target) {
                    if (target.kind !== 'audio') {
                        console.warn('Expected an audio message for transcription update', target);
                        return;
                    }
                    console.log('updating existing transcript', data.item_id, data.transcript, target);
                    const updated = { ...target, transcript: data.transcript };
                    messageStore.put(updated);
                    emit('update', updated);
                }
                else {
                    console.warn('Could not find message for transcription event with item_id:', data.item_id);
                }
            }
            return;
        }
        // console.info('Unhandled message type', data);
    };
    // Simple event handlers map.
    const events = {};
    const emit = (event, data) => {
        (events[event] || []).forEach((handler) => {
            if (event === 'ready') {
                // ready event has no payload.
                // @ts-expect-error
                handler();
            }
            else {
                if (data === undefined) {
                    throw new Error(`Data is required for event ${event}`);
                }
                handler(data);
            }
        });
    };
    // Helper to send data over the connection.
    function sendRaw(event) {
        const raw = serdes.serialize(event);
        connection.send(raw);
    }
    const CALL_IDS = {
        checkpoint: 'checkpoint',
    };
    const tools = {};
    return {
        sendRaw,
        async call(props) {
            return new Promise((resolve, reject) => {
                const callId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`; // Generate a unique call ID
                console.log('calling', props.name, callId);
                pendingFunctionCalls[callId] = { resolve, reject };
                // Send the function call request to the server
                sendRaw({
                    type: 'response.create',
                    response: {
                        // conversation: 'none',
                        input: [
                            {
                                type: 'function_call',
                                name: props.name,
                                call_id: callId,
                                arguments: '{}', // Add arguments if needed
                            },
                        ],
                    },
                });
            });
        },
        async sendDirective(props) {
            console.log('Creating directive with separate tracking mechanism');
            // Generate a unique ID for this directive
            const directiveId = `directive_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
            // Create a pending promise specifically for this directive
            return new Promise((resolve, reject) => {
                // Add to our separate tracking mechanism
                pendingDirectives.push({
                    id: directiveId,
                    resolve,
                    reject,
                });
                console.log(`Added directive ${directiveId} to pendingDirectives, now length:`, pendingDirectives.length);
                // Send the directive (system message)
                sendRaw({
                    type: 'response.create',
                    response: {
                        input: [
                            {
                                type: 'message',
                                // role: 'system',
                                role: props.role,
                                content: [
                                    {
                                        type: 'input_text',
                                        text: props.text,
                                    },
                                ],
                            },
                        ],
                    },
                });
                // Set a timeout to resolve this directive after 3 seconds if not resolved already
                // This is a fallback to ensure the UI doesn't hang indefinitely
                setTimeout(() => {
                    const index = pendingDirectives.findIndex((d) => d.id === directiveId);
                    if (index !== -1) {
                        console.log(`Resolving directive ${directiveId} after timeout`);
                        const directive = pendingDirectives.splice(index, 1)[0];
                        if (directive) {
                            directive.resolve();
                        }
                    }
                }, 3000);
            });
        },
        async start(props) {
            debug('Connecting...');
            connection.addHandler(handleRawMessage);
            connection.connect();
            // Once the connection is established, flush any buffered messages
            isConnected = true;
            bufferedMessages.forEach((pending) => {
                (0, util_typescript_1.assert)(pending.userMessage?.kind === 'text', 'pending message not found');
                messageStore.put(pending.userMessage);
                emit('message', pending.userMessage);
                sendRaw({
                    type: 'conversation.item.create',
                    item: {
                        type: 'message',
                        role: 'user',
                        content: [
                            { type: 'input_text', text: pending.userMessage.content },
                        ],
                    },
                });
                sendRaw({ type: 'response.create' });
            });
            bufferedMessages = []; // Clear the buffer after sending
        },
        async send(props) {
            const tempId = generateTempId();
            const placeholder = (0, message_1.buildMessage)({ id: tempId, ...props });
            // If not connected, buffer the message
            if (!isConnected) {
                const pendingPromise = new Promise((resolve) => {
                    bufferedMessages.push({
                        userMessage: placeholder,
                        assistantReplies: [],
                        resolve,
                    });
                });
                messageStore.put(placeholder);
                emit('message', placeholder);
                return pendingPromise;
            }
            // If connected, send the message immediately
            messageStore.put(placeholder);
            emit('message', placeholder);
            if (placeholder.kind === 'text') {
                sendRaw({
                    type: 'conversation.item.create',
                    item: {
                        type: 'message',
                        role: 'user',
                        content: [{ type: 'input_text', text: placeholder.content }],
                    },
                });
            }
            sendRaw({ type: 'response.create' });
            return Promise.resolve({
                userMessage: placeholder,
                assistantReplies: [],
            });
        },
        async stop() {
            debug('Stopping...');
            connection.disconnect();
            isConnected = false;
        },
        async update(props) {
            const payload = {
                type: 'session.update',
                session: {
                    tool_choice: 'auto',
                    tools: [
                        // ...Object.values(tools),
                        ...props.tools,
                    ].map((tool) => ({
                        type: 'function',
                        name: tool.definition.name,
                        description: tool.definition.description,
                        parameters: tool.definition.parameters,
                    })),
                },
            };
            console.log('updating session', payload);
            sendRaw(payload);
        },
        async reply() {
            const pendingPromise = new Promise((resolve) => {
                pendingRequests.push({
                    userMessage: null,
                    assistantReplies: [],
                    resolve,
                });
            });
            sendRaw({
                type: 'response.create',
            });
            return pendingPromise;
        },
        on(event, handler) {
            if (!events[event]) {
                events[event] = [];
            }
            events[event].push(handler);
        },
        off(event, handler) {
            if (!events[event])
                return;
            // @ts-expect-error
            events[event] = events[event].filter((h) => h !== handler);
        },
    };
}
function createMemoryAdapter(config) {
    const state = {
        byId: (0, util_typescript_1.keyBy)(config.initial, 'id'),
        ids: config.initial.map((m) => m.id),
    };
    return {
        scan() {
            return state.ids.map((id) => {
                const entity = state.byId[id];
                (0, util_typescript_1.assert)(entity, 'message not found');
                return entity;
            });
        },
        put(message) {
            if (!state.byId[message.id]) {
                state.ids.push(message.id);
            }
            state.byId[message.id] = message;
        },
        delete(id) {
            delete state.byId[id];
            state.ids = state.ids.filter((x) => x !== id);
        },
    };
}
function createWebRTCAdapter(config) {
    const { channel } = config;
    const handlers = new Set();
    function internalMessageHandler(event) {
        for (const handler of handlers) {
            handler(event.data);
        }
    }
    return {
        getState() {
            return { status: 'connected' };
        },
        connect() {
            console.log('WebRTC connection established');
            channel.onmessage = internalMessageHandler;
        },
        disconnect() {
            channel.onmessage = null;
            handlers.clear();
            // channel.close();
        },
        send(data) {
            channel.send(data);
        },
        addHandler(handler) {
            handlers.add(handler);
        },
        removeHandler(handler) {
            handlers.delete(handler);
        },
    };
}
function createJsonSerdes() {
    return {
        serialize(data) {
            return JSON.stringify(data);
        },
        deserialize(raw) {
            return JSON.parse(raw);
        },
    };
}
//# sourceMappingURL=clientProvider.js.map